import { InventorySKUListing } from '@views/StockBySKU/components/types';
import { FacetOption } from '@tailwindComponents/CheckboxFacetGroup';

export type SkuFacetState = {
  category: string | null;
  subCategory: string | null;
  unassigned: boolean;
  brands: Set<string>;
};

const categoryValueOf = (sku: InventorySKUListing): string | null =>
  (sku.productCategory ?? sku.productDetails?.category)?.trim() || null;

const subCategoryValueOf = (sku: InventorySKUListing): string | null =>
  (sku.productSubCategory ?? sku.productDetails?.subCategory)?.trim() || null;

export const skuBrand = (sku: InventorySKUListing): string | null =>
  (sku.brand ?? sku.productDetails?.brand)?.trim() || null;

export const emptySkuFacetState = (): SkuFacetState => ({
  category: null,
  subCategory: null,
  unassigned: false,
  brands: new Set(),
});

export const hasActiveHierarchy = (facets: SkuFacetState): boolean =>
  facets.unassigned || facets.category !== null;

export const hasActiveFacets = (facets: SkuFacetState): boolean =>
  hasActiveHierarchy(facets) || facets.brands.size > 0;

const matchesBrand = (
  sku: InventorySKUListing,
  facets: SkuFacetState
): boolean => {
  if (!facets.brands.size) return true;
  const value = skuBrand(sku);
  return value !== null && facets.brands.has(value);
};

const matchesHierarchy = (
  sku: InventorySKUListing,
  facets: SkuFacetState
): boolean => {
  if (facets.unassigned) return categoryValueOf(sku) === null;
  if (facets.category !== null && categoryValueOf(sku) !== facets.category)
    return false;
  if (
    facets.subCategory !== null &&
    subCategoryValueOf(sku) !== facets.subCategory
  )
    return false;
  return true;
};

export const applyFacets = (
  skus: InventorySKUListing[],
  facets: SkuFacetState
): InventorySKUListing[] =>
  skus.filter(
    (sku) => matchesBrand(sku, facets) && matchesHierarchy(sku, facets)
  );

export const deriveBrandOptions = (
  skus: InventorySKUListing[],
  facets: SkuFacetState
): FacetOption[] => {
  const counts = new Map<string, number>();
  skus.forEach((sku) => {
    if (!matchesHierarchy(sku, facets)) return;
    const value = skuBrand(sku);
    if (value === null) return;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  });
  return [...counts.entries()]
    .map(([value, count]) => ({ value, label: value, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const toggleBrand = (
  facets: SkuFacetState,
  value: string
): SkuFacetState => {
  const brands = new Set(facets.brands);
  if (brands.has(value)) {
    brands.delete(value);
  } else {
    brands.add(value);
  }
  return { ...facets, brands };
};

export const selectBrand = (
  facets: SkuFacetState,
  value: string | null
): SkuFacetState => ({
  ...facets,
  brands: value === null ? new Set() : new Set([value]),
});

const clearHierarchy = (facets: SkuFacetState): SkuFacetState => ({
  ...facets,
  category: null,
  subCategory: null,
  unassigned: false,
});

export const selectAllProducts = clearHierarchy;

export const selectUnassigned = (facets: SkuFacetState): SkuFacetState => ({
  ...clearHierarchy(facets),
  unassigned: true,
});

export const selectCategory = (
  facets: SkuFacetState,
  category: string
): SkuFacetState => ({
  ...facets,
  category,
  subCategory: null,
  unassigned: false,
});

export const selectSubCategory = (
  facets: SkuFacetState,
  category: string,
  subCategory: string
): SkuFacetState => ({
  ...facets,
  category,
  subCategory,
  unassigned: false,
});

export const pruneFacetState = (
  facets: SkuFacetState,
  skus: InventorySKUListing[]
): SkuFacetState => {
  const brandsPresent = new Set(
    skus.map(skuBrand).filter((value): value is string => value !== null)
  );
  const categoriesPresent = new Set(
    skus.map(categoryValueOf).filter((value): value is string => value !== null)
  );
  const subCategoriesPresent = new Set(
    skus
      .map(subCategoryValueOf)
      .filter((value): value is string => value !== null)
  );
  const category =
    facets.category !== null && categoriesPresent.has(facets.category)
      ? facets.category
      : null;
  const subCategory =
    category !== null &&
    facets.subCategory !== null &&
    subCategoriesPresent.has(facets.subCategory)
      ? facets.subCategory
      : null;
  return {
    unassigned: facets.unassigned,
    category,
    subCategory,
    brands: new Set(
      [...facets.brands].filter((value) => brandsPresent.has(value))
    ),
  };
};

export type SkuHierarchyNode = {
  name: string;
  count: number;
  subCategories: { name: string; count: number }[];
};

export type SkuHierarchyTree = {
  total: number;
  unassigned: number;
  categories: SkuHierarchyNode[];
};

export const buildHierarchyTree = (
  skus: InventorySKUListing[],
  facets: SkuFacetState
): SkuHierarchyTree => {
  const inScope = skus.filter((sku) => matchesBrand(sku, facets));
  const categoryEntries = new Map<string, Map<string, number>>();
  const categoryTotals = new Map<string, number>();
  let unassigned = 0;

  inScope.forEach((sku) => {
    const category = categoryValueOf(sku);
    if (category === null) {
      unassigned += 1;
      return;
    }
    categoryTotals.set(category, (categoryTotals.get(category) ?? 0) + 1);
    const subCategory = subCategoryValueOf(sku);
    if (subCategory === null) return;
    const subCounts = categoryEntries.get(category) ?? new Map();
    subCounts.set(subCategory, (subCounts.get(subCategory) ?? 0) + 1);
    categoryEntries.set(category, subCounts);
  });

  const categories = [...categoryTotals.entries()]
    .map(([name, count]) => ({
      name,
      count,
      subCategories: [...(categoryEntries.get(name) ?? new Map()).entries()]
        .map(([subName, subCount]) => ({ name: subName, count: subCount }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { total: inScope.length, unassigned, categories };
};
